import { set as setCookie } from 'js-cookie';
import { useCallback, useEffect, useState } from 'react';

import { useSelectedEmployeeId } from '~/hooks/employee/useSelectedEmployeeId';
import { useSyncedLocalStorage } from '~/hooks/useLocalStorage';
import { useVisible } from '~/hooks/useVisible';

const OPERATION_PAGE_ID = 'Operations-Page-Id';
const ID_PAGE_OVERVIEW = 'idPageOverview';
const OPERATIONS_PAGE_NUM = 'Operations-Page-Num';

export const ALL_EMPS_SEARCH_TAB_INDEX = 1;
export const TEAM_SEARCH_TAB_INDEX = 2;
export const MATRIX_TEAM_SEARCH_TAB_INDEX = 3;
export const ORG_UNITS_SEARCH_TAB_INDEX = 4;
export const NEW_EMPS_SEARCH_TAB_INDEX = 5;
export const ENDED_EMPS_SEARCH_TAB_INDEX = 6;
export const RESULTS_SEARCH_TAB_INDEX = 7;
export const SEND_EMAIL_SEARCH_TAB_INDEX = 8;
export const HR_ADMIN_OVERVIEW_SEARCH_TAB_INDEX = 9;

export type UseEmployeesSearchOutput = {
  currentTab: number;
  isBoxOpen: boolean;
  isPinned: boolean;
  openBox: () => void;
  toggleBox: () => void;
  onClose: (force?: boolean) => void;
  onCurrentTabChange: (tabIndex: number) => void;
  onPin: () => void;
};

export const useSearchUI = (
  onEmailClick: () => void,
): UseEmployeesSearchOutput => {
  const { value: isPinned, setValue: setIsPinned } =
    useSyncedLocalStorage<boolean>('searchIsPinned', false);
  const {
    isVisible: isBoxOpen,
    show: openBox,
    hide: closeBox,
    toggle: toggleBox,
    setIsVisible,
  } = useVisible(false, isPinned);

  useEffect(() => {
    if (!isBoxOpen && isPinned) {
      setIsVisible(true);
    }
  }, [setIsVisible, isBoxOpen, isPinned]);

  const { selectedEmployeeId } = useSelectedEmployeeId();
  const [currentTab, setCurrentTab] = useState<number>(1);
  const onClose = useCallback(
    (force?: boolean) => {
      if (!force && isPinned) {
        return;
      }

      setIsPinned(false);
      closeBox();
    },
    [isPinned, closeBox, setIsPinned],
  );

  const onTogglePin = useCallback(() => {
    setIsPinned(!isPinned);
  }, [isPinned, setIsPinned]);

  const onCurrentTabChange = useCallback(
    (tabIndex: number) => {
      if (tabIndex === HR_ADMIN_OVERVIEW_SEARCH_TAB_INDEX) {
        setCookie(OPERATIONS_PAGE_NUM, '0');
        setCookie(OPERATION_PAGE_ID, ID_PAGE_OVERVIEW);
        window.location.replace(
          `/page-admin-operations.html?emp=${selectedEmployeeId}`,
        );

        return;
      }

      if (tabIndex === SEND_EMAIL_SEARCH_TAB_INDEX) {
        onEmailClick();
        return;
      }

      setCurrentTab(tabIndex);
    },
    [selectedEmployeeId, onEmailClick, setCurrentTab],
  );

  return {
    currentTab,
    isBoxOpen,
    isPinned,
    openBox,
    toggleBox,
    onClose,
    onCurrentTabChange,
    onPin: onTogglePin,
  };
};
